/* ==========================================================================
   VLB PDP — self-contained JS for the rebuilt product page.
   Vanilla custom elements following the theme's existing patterns. Relies
   only on globals the theme ships on every page: pubsub.js (publish/
   subscribe), constants.js (PUB_SUB_EVENTS), global.js (fetchConfig) and
   window.routes from layout/theme.liquid. Never touches old-PDP code.
   ========================================================================== */

/* Respect the OS motion preference for all JS-driven scrolling; read
   .matches at call time so mid-session preference changes are honored. */
const VLB_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/* --- Add to Bag ----------------------------------------------------------
   Posts to /cart/add.js with the cart-drawer's sections so the existing
   global drawer re-renders and opens — the same contract product forms use
   across this theme. */

if (!customElements.get('vlb-product-form')) {
    customElements.define(
        'vlb-product-form',
        class VlbProductForm extends HTMLElement {
            constructor() {
                super();
                this.form = this.querySelector('form');
                this.submitButton = this.querySelector('[data-vlb-atc]');
                this.errorBox = this.querySelector('[data-vlb-form-error]');
                this.cart =
                    document.querySelector('cart-notification') || document.querySelector('cart-drawer');

                const idInput = this.form.querySelector('[data-vlb-variant-input]');
                if (idInput && this.submitButton && !this.submitButton.hasAttribute('disabled')) {
                    idInput.disabled = false;
                }
                this.form.addEventListener('submit', this.onSubmit.bind(this));
                document.addEventListener('click', this.shopifyPaymentButtonsHandler.bind(this), true);
            }

            shopifyPaymentButtonsHandler(evt) {
                if (evt.target.matches(".shopify-payment-button") || evt.target.closest('.shopify-payment-button')) {
                    if (!this.hasSelectedSize()) {
                        evt.stopPropagation();
                        this.onSubmit(evt);
                    }
                }
            }

            updatePDPInCartCount(data){
                if(!location.pathname.toLowerCase().includes("/products/")) return;
                const URLSplit = location.pathname.split("/");
                const handle = URLSplit[URLSplit.length - 1].trim();
                if(data.handle !== handle) return;
                const target = document.querySelector('.product-form__quantity .quantity-cart');
                if(!target) return;
                target.innerText = data.quantity;
                document.querySelector(".quantity__rules-cart").classList.remove('hidden');
            }

            hasSelectedSize() {
                // Gate only when this product actually has size options. A vlb
                // product with no size swatches — a single-variant product
                // (has_only_default_variant renders none) or one whose option
                // isn't named "Size" — has nothing to pick, so it must not be
                // blocked. Scope to this vlb-product-info so multiple instances
                // (e.g. a quick view) never read each other's state.
                const info = this.closest('vlb-product-info');
                const scope = info || document;
                const sizes = [].slice.call(scope.querySelectorAll('.vlb-swatches__input.size'));
                if (!sizes.length) return true;

                const variantInput = scope.querySelector("[data-vlb-variant-input]");

                const sizeChecked = sizes.some(function (size) {
                    return size.checked;
                });
                if (!sizeChecked || !variantInput || !variantInput.value) return false;

                // Defense-in-depth: never trust a merely-non-empty id. Resolve the
                // variant from the currently checked options and require the id that
                // will be submitted to match it, so a stale/default (lowest) id can
                // never slip through to /cart/add or accelerated checkout.
                if (info && typeof info.resolveVariant === 'function') {
                    const resolved = info.resolveVariant(info.selectedOptions());
                    if (resolved && String(resolved.id) !== String(variantInput.value)) return false;
                }
                return true;
            }

            onSubmit(evt) {
                evt.preventDefault();
                if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
                if (this.submitButton.hasAttribute('disabled')) return;

                const selectSizeMessage = document.querySelector('.vlb-select-size-message');

                const hasChecked = this.hasSelectedSize();

                selectSizeMessage && selectSizeMessage.classList.toggle('hidden', hasChecked);

                if (!hasChecked) {
                    selectSizeMessage && selectSizeMessage.scrollIntoView({
                        block: 'center',
                        inline: 'center',
                        behavior: VLB_REDUCED_MOTION.matches ? 'auto' : 'smooth',
                    });
                    return;
                }

                // The sticky bar's button submits this form via its form= attribute;
                // show loading feedback on whichever button the user actually pressed.
                this.activeButton = evt.submitter || this.submitButton;

                this.toggleError(false);
                this.submitButton.setAttribute('aria-disabled', 'true');
                this.activeButton.setAttribute('aria-disabled', 'true');
                this.activeButton.classList.add('loading');
                this.activeButton.querySelector('.loading__spinner')?.classList.remove('hidden');

                const config = fetchConfig('javascript');
                config.headers['X-Requested-With'] = 'XMLHttpRequest';
                delete config.headers['Content-Type'];

                const formData = new FormData(this.form);
                if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
                    formData.append(
                        'sections',
                        this.cart.getSectionsToRender().map((section) => section.id)
                    );
                    formData.append('sections_url', window.location.pathname);
                    if (typeof this.cart.setActiveElement === 'function') {
                        this.cart.setActiveElement(document.activeElement);
                    }
                }
                config.body = formData;

                const quantitySpinner = document.querySelector('.product-form__quantity .loading__spinner');
                if(quantitySpinner) {
                    quantitySpinner.classList.remove('hidden');
                }

                fetch(`${routes.cart_add_url}`, config)
                    .then((response) => response.json())
                    .then((response) => {
                        if (response.status) {
                            publish(PUB_SUB_EVENTS.cartError, {
                                source: 'vlb-product-form',
                                productVariantId: formData.get('id'),
                                errors: response.errors || response.description,
                                message: response.message,
                            });
                            this.toggleError(true, response.description || response.message);
                            // /cart/add can change the cart even when it responds with an
                            // error: requesting more than the available stock adds what's
                            // left and then reports "you can only add N". Re-sync the
                            // mini-cart from the server so it reflects the real cart.
                            this.refreshMiniCart(formData.get('id'));
                            if(quantitySpinner) {
                                quantitySpinner.classList.add('hidden');
                            }
                            return;
                        }

                        if (!this.cart) {
                            window.location = window.routes.cart_url;
                            return;
                        }

                        publish(PUB_SUB_EVENTS.cartUpdate, {
                            source: 'product-form',
                            productVariantId: formData.get('id'),
                            cartData: response,
                        });
                        this.cart.renderContents(response);
                        this.updatePDPInCartCount(response);
                        if(quantitySpinner) {
                            quantitySpinner.classList.add('hidden');
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                        this.toggleError(true, window.cartStrings?.error);
                    })
                    .finally(() => {
                        const button = this.activeButton || this.submitButton;
                        button.classList.remove('loading');
                        button.removeAttribute('aria-disabled');
                        this.submitButton.removeAttribute('aria-disabled');
                        button.querySelector('.loading__spinner')?.classList.add('hidden');
                        if (this.cart && this.cart.classList.contains('is-empty')) {
                            this.cart.classList.remove('is-empty');
                        }
                    });
            }

            refreshMiniCart(variantId) {
                // Re-render the mini-cart sections so the icon bubble and drawer
                // contents match the server cart, and open the drawer when items
                // actually went in despite the error.
                if (!this.cart || typeof this.cart.getSectionsToRender !== 'function') return;

                // The icon bubble hasn't been re-rendered on the error path yet,
                // so it still shows the pre-add count — capture it to tell a
                // partial add (cart grew) from a pure failure (e.g. sold out).
                const previousCount = this.getCartIconCount();

                const sectionsToRender = this.cart.getSectionsToRender();
                const sectionIds = sectionsToRender.map((section) => section.id);
                Promise.all([
                    fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`).then((response) =>
                        response.json()
                    ),
                    fetch(`${routes.cart_url}.js`).then((response) => response.json()),
                ])
                    .then(([sections, cart]) => {
                        sectionsToRender.forEach((section) => {
                            const sectionElement = section.selector
                                ? document.querySelector(section.selector)
                                : document.getElementById(section.id);
                            if (!sectionElement || !sections[section.id]) return;
                            sectionElement.innerHTML = this.cart.getSectionInnerHTML(
                                sections[section.id],
                                section.selector
                            );
                        });
                        // The drawer overlay is inside the replaced section — re-bind
                        // its click-to-close, as renderContents does.
                        const overlay = this.cart.querySelector('#CartDrawer-Overlay');
                        if (overlay) overlay.addEventListener('click', this.cart.close.bind(this.cart));

                        this.cart.classList.toggle('is-empty', cart.item_count === 0);
                        const line = cart.items.find((item) => String(item.variant_id) === String(variantId));
                        if (line) this.updatePDPInCartCount({handle: line.handle, quantity: line.quantity});

                        // Items were added despite the error (partial add) — surface
                        // the drawer so the shopper sees what actually went in. Only
                        // the drawer: the notification renders a specific line item
                        // we don't have a key for on this path.
                        if (cart.item_count > previousCount && this.cart.localName === 'cart-drawer') {
                            this.cart.open();
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            }

            getCartIconCount() {
                const bubbleText = document.querySelector('#cart-icon-bubble .cart-count-bubble .visually-hidden');
                if (!bubbleText) return 0;
                const match = bubbleText.textContent.match(/\d+/);
                return match ? parseInt(match[0], 10) : 0;
            }

            toggleError(show, message) {
                if (!this.errorBox) return;
                this.errorBox.hidden = !show;
                if (message) this.errorBox.textContent = message;
                // Errors render in the buy box; if the user submitted from the sticky
                // bar the box is offscreen — bring it back (this also auto-hides the
                // sticky bar via its IntersectionObserver).
                if (show && this.errorBox.getBoundingClientRect().bottom < 0) {
                    this.errorBox.scrollIntoView({
                        block: 'center',
                        behavior: VLB_REDUCED_MOTION.matches ? 'auto' : 'smooth',
                    });
                }
            }
        }
    );
}

/* --- Variant orchestration -------------------------------------------------
   Re-render contract: option changes resolve the target variant from the
   embedded variants JSON, then re-fetch this section via the Section
   Rendering API (?variant=<id>&section_id=<id>) and swap the innerHTML of
   the whitelisted [data-vlb-swap] regions with server-rendered truth
   (price, installments, options/availability, back-in-stock, sticky info).
   Option inputs use delegated listeners so they survive their own swap.
   Publishes PUB_SUB_EVENTS.variantChange for the sticky bar and any other
   subscriber. */

if (!customElements.get('vlb-product-info')) {
    customElements.define(
        'vlb-product-info',
        class VlbProductInfo extends HTMLElement {
            static SWAP_REGIONS = ['price', 'installments', 'options', 'bis', 'sticky-info'];

            constructor() {
                super();
                this.abortController = undefined;
                try {
                    this.variants = JSON.parse(this.querySelector('[data-vlb-variants]').textContent);
                } catch (e) {
                    this.variants = [];
                }
                this.addEventListener('change', (e) => {
                    if (e.target.matches('[data-vlb-option-input]')) this.onOptionChange(e.target.id);
                });
                // Back-in-stock submits are delegated here so the form survives
                // the options/bis region swaps on variant change.
                this.addEventListener('submit', (e) => {
                    const form = e.target.closest('[data-vlb-bis-form]');
                    if (form) {
                        e.preventDefault();
                        this.subscribeBackInStock(form);
                    }
                });
            }

            selectedOptions() {
                return Array.from(this.querySelectorAll('[data-vlb-option-fieldset]')).map(
                    (fieldset) => fieldset.querySelector('[data-vlb-option-input]:checked')?.value
                );
            }

            resolveVariant(selected) {
                return this.variants.find((variant) =>
                    selected.every((value, i) => (variant[`option${i + 1}`] || undefined) === value)
                );
            }

            onOptionChange(targetId) {
                const variant = this.resolveVariant(this.selectedOptions());
                if (!variant) {
                    this.setAtcState(false, window.variantStrings?.unavailable || 'Unavailable');
                    return;
                }
                this.updateVariantInputAndATC(variant);
                window.dispatchEvent(new CustomEvent('variantChange', {detail: {variant: variant}}));
                this.renderVariant(variant, targetId)
                    .then(() => {});
            }

            /* Klaviyo public client API — records a back-in-stock subscription
               against the Shopify variant. The company id is read from the
               Klaviyo onsite script the app embed injects on every page. */
            static klaviyoCompanyId() {
                const script = document.querySelector('script[src*="klaviyo.js"][src*="company_id="]');
                if (!script) return null;
                try {
                    return new URL(script.src).searchParams.get('company_id');
                } catch (e) {
                    return null;
                }
            }

            async subscribeBackInStock(form) {
                const message = this.querySelector('[data-vlb-bis-msg]');
                const button = form.querySelector('[type="submit"]');
                const email = form.querySelector('input[type="email"]')?.value.trim();
                const companyId = VlbProductInfo.klaviyoCompanyId();
                if (!email || !button) return;

                const fail = () => {
                    if (message) {
                        message.textContent = form.dataset.errorText || 'Something went wrong — please try again.';
                        message.hidden = false;
                    }
                };

                if (!companyId) {
                    console.error('vlb-bis: Klaviyo onsite script not found — cannot resolve company_id');
                    fail();
                    return;
                }

                button.setAttribute('aria-disabled', 'true');
                button.classList.add('loading');
                if (message) message.hidden = true;

                try {
                    const response = await fetch(
                        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
                        {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json', revision: '2024-10-15'},
                            body: JSON.stringify({
                                data: {
                                    type: 'back-in-stock-subscription',
                                    attributes: {
                                        channels: ['EMAIL'],
                                        profile: {data: {type: 'profile', attributes: {email: email}}},
                                    },
                                    relationships: {
                                        variant: {
                                            data: {
                                                type: 'catalog-variant',
                                                id: `$shopify:::$default:::${form.dataset.variantId}`,
                                            },
                                        },
                                    },
                                },
                            }),
                        }
                    );
                    if (!response.ok) throw new Error(`Klaviyo BIS ${response.status}`);
                    form.hidden = true;
                    if (message) {
                        message.textContent =
                            form.dataset.successText || "You're on the list — we'll email you the moment it's back.";
                        message.classList.add('vlb-bis__msg--success');
                        message.hidden = false;
                    }
                } catch (error) {
                    console.error(error);
                    fail();
                } finally {
                    button.classList.remove('loading');
                    button.removeAttribute('aria-disabled');
                }
            }

            setAtcState(available, text) {
                this.querySelectorAll('[data-vlb-atc]').forEach((button) => {
                    button.toggleAttribute('disabled', !available);
                    const label = button.querySelector('[data-vlb-atc-text]');
                    if (label && text) label.textContent = text;
                });
                this.querySelectorAll('[data-vlb-variant-input]').forEach((input) => {
                    input.disabled = !available;
                });
            }

            updateVariantInputAndATC(variant, ATCText){
                this.querySelectorAll('[data-vlb-variant-input]').forEach((input) => {
                    input.value = variant.id;
                    input.disabled = !variant.available;
                    // Notify Shopify's hydrated dynamic-checkout (Shop Pay / Apple
                    // Pay / PayPal / GPay) button that the variant changed — it only
                    // re-reads the form's id on a bubbling `change`. Without this the
                    // wallet stays pinned to the seeded default (lowest) variant.
                    // Loop-safe: the option listener only reacts to
                    // [data-vlb-option-input] and the sticky bar only to name==='quantity'.
                    input.dispatchEvent(new Event('change', {bubbles: true}));
                });
                this.setAtcState(variant.available, ATCText);
            }

            async renderVariant(variant, targetId) {
                this.abortController?.abort();
                const controller = new AbortController();
                this.abortController = controller;
                this.classList.add('vlb-updating');

                const url = `${this.dataset.productUrl}?variant=${variant.id}&section_id=${this.dataset.section}`;
                try {
                    const response = await fetch(url, {signal: controller.signal});
                    const text = await response.text();
                    const doc = new DOMParser().parseFromString(text, 'text/html');

                    VlbProductInfo.SWAP_REGIONS.forEach((region) => {
                        const source = doc.querySelector(`[data-vlb-swap="${region}"]`);
                        const destination = this.querySelector(`[data-vlb-swap="${region}"]`);
                        if (source && destination) destination.innerHTML = source.innerHTML;
                    });

                    // The options swap re-renders identical deterministic input ids —
                    // restore keyboard focus to the radio the user just activated.
                    if (targetId) this.querySelector(`#${CSS.escape(targetId)}`)?.focus();

                    // Form state: variant id + ATC availability, mirrored from the
                    // server-rendered button so copy stays Liquid-owned.
                    const freshAtc = doc.querySelector('[data-vlb-atc]');
                    this.updateVariantInputAndATC(variant, freshAtc?.querySelector('[data-vlb-atc-text]')?.textContent.trim());

                    // Gallery: show this variant's image first.
                    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
                    if (mediaId) {
                        this.querySelector('vlb-gallery')?.setActiveMedia(mediaId, true);
                    }

                    window.history.replaceState({}, '', `${this.dataset.productUrl}?variant=${variant.id}`);
                    this.dataset.variantId = String(variant.id);

                    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                        publish(PUB_SUB_EVENTS.variantChange, {
                            data: {sectionId: this.dataset.section, variant: variant},
                        });
                    }
                } catch (error) {
                    if (error.name !== 'AbortError') console.error(error);
                } finally {
                    // Only the request that still owns the controller clears the
                    // loading state — an aborted call must not strip it from the
                    // superseding in-flight request.
                    if (this.abortController === controller) this.classList.remove('vlb-updating');
                }
            }
        }
    );
}

/* --- Sticky mobile Add to Bag ----------------------------------------------
   Shows the bar once the buy box has scrolled above the viewport; hides it
   again when the buy box is visible (or the user is above it). Fixed
   overlay — never shifts layout. */

if (!customElements.get('vlb-sticky-atc')) {
    customElements.define(
        'vlb-sticky-atc',
        class VlbStickyAtc extends HTMLElement {
            observer;
            boundQuantityChange;
            boundVariantChange;
            priceElement;
            priceFormatter;
            price;

            constructor() {
                super();
                this.priceElement = this.querySelector(".vlb-sticky__meta");
                if (this.priceElement) {
                    this.price = this.extractPrice(this.priceElement.textContent);
                    this.boundQuantityChange = this.observerQuantityChange.bind(this);
                    this.boundVariantChange = this.onVariantChange.bind(this);
                    this.priceFormatter = new Intl.NumberFormat(Shopify.locale + "-" + Shopify.country,
                        {
                            style: 'decimal',
                            currency: Shopify.currency.active,
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        });
                }
            }

            extractPrice(price) {
                return parseFloat(price.replace(/[^0-9.-]+/g, ""));
            }

            onVariantChange() {
                this.priceElement.textContent = this.getFormattedPrice(this.price);
            }

            getFormattedPrice(price) {
                return window.moneyWithCurrencyFormat
                    .replace("{{amount}}", this.priceFormatter.format(price))
                    .replace(Shopify.currency.active, "")
                    .trim();
            }

            observerQuantityChange(e) {
                if (e.target.tagName === 'INPUT' && e.target.name === 'quantity') {
                    const value = parseInt(e.target.value);
                    const newPrice = this.price * value;
                    this.priceElement.textContent = this.getFormattedPrice(newPrice);
                }
            }

            initObserver() {
                const buyBox = this.closest('vlb-product-info')?.querySelector('.vlb-buy');
                if (!buyBox || !('IntersectionObserver' in window)) return;
                this.observer = new IntersectionObserver(
                    (entries) => {
                        // records arrive oldest-first — the last one is current state
                        const entry = entries[entries.length - 1];
                        const passed = !entry.isIntersecting;
                        // && entry.boundingClientRect.bottom < 0;
                        this.classList.toggle('is-visible', passed);
                        if (passed) document.documentElement.style.setProperty('--sticky-atc-height', `${this.offsetHeight}px`);
                        document.documentElement.classList.toggle('sticky-atc-visible', passed);
                    },
                    {threshold: 0}
                );
                this.observer.observe(buyBox);
            }

            connectedCallback() {
                this.initObserver();
                window.addEventListener('change', this.boundQuantityChange);
                window.addEventListener('variantChange', this.boundVariantChange);
            }

            disconnectedCallback() {
                this.observer?.disconnect();
                window.removeEventListener('change', this.boundQuantityChange);
                window.removeEventListener('variantChange', this.boundVariantChange);
            }
        }
    );
}

/* --- Gallery --------------------------------------------------------------
   Scroll-snap track + thumbnail sync, exposes setActiveMedia(mediaId) for
   variant changes, and a zoom lightbox with double-tap and two-finger pinch. */

if (!customElements.get('vlb-gallery')) {
    customElements.define(
        'vlb-gallery',
        class VlbGallery extends HTMLElement {
            constructor() {
                super();
                this.track = this.querySelector('[data-vlb-track]');
                this.slides = Array.from(this.querySelectorAll('[data-vlb-slide]'));
                this.thumbs = Array.from(this.querySelectorAll('[data-vlb-thumb]'));
                this.thumbsRow = this.querySelector('[data-vlb-thumbs]');
                this.lightbox = this.querySelector('[data-vlb-lightbox]');
                this.stage = this.querySelector('[data-vlb-lightbox-stage]');

                // Teardown on the dialog's native close event so EVERY close path
                // (close button, Escape/cancel, in-app browser back gesture) clears
                // the scroll lock and stops any playing lightbox video.
                this.lightbox?.addEventListener('close', () => {
                    if (this.stage) this.stage.innerHTML = '';
                    document.documentElement.style.overflow = '';
                });

                if (this.track) {
                    this.track.addEventListener('scroll', () => {
                        window.requestAnimationFrame(() => this.syncActiveFromScroll());
                    });
                }
                this.thumbs.forEach((thumb) => {
                    thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.mediaId, true));
                });
                this.querySelectorAll('[data-vlb-zoom]').forEach((btn) => {
                    btn.addEventListener('click', () => this.openLightbox(btn.dataset.mediaId));
                });
                this.querySelector('[data-vlb-lightbox-close]')?.addEventListener('click', () =>
                    this.closeLightbox()
                );
                // The stage fills the dialog, so "backdrop" taps land on the stage
                // itself — close when a clean tap (not the end of a drag) missed
                // the media.
                this.stage?.addEventListener('click', (e) => {
                    if (e.target === this.stage && !this.gestureMoved) this.closeLightbox();
                });
                this.initGestures();
            }

            slideIndexFor(mediaId) {
                return this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
            }

            syncActiveFromScroll() {
                if (!this.slides.length) return;
                const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
                const slide = this.slides[Math.min(index, this.slides.length - 1)];
                if (slide) this.markActive(slide.dataset.mediaId);
            }

            markActive(mediaId) {
                this.thumbs.forEach((t) => {
                    const active = t.dataset.mediaId === String(mediaId);
                    t.classList.toggle('is-active', active);
                    if (active) {
                        t.setAttribute('aria-current', 'true');
                        // Scroll only the thumb strip — never scrollIntoView, which
                        // would also scroll the page and yank the viewport away from
                        // the swatches the user is interacting with.
                        if (this.thumbsRow) {
                            this.thumbsRow.scrollTo({
                                left:
                                    t.offsetLeft -
                                    this.thumbsRow.offsetLeft -
                                    (this.thumbsRow.clientWidth - t.offsetWidth) / 2,
                                behavior: VLB_REDUCED_MOTION.matches ? 'auto' : 'smooth',
                            });
                        }
                    } else {
                        t.removeAttribute('aria-current');
                    }
                });
            }

            setActiveMedia(mediaId, smooth) {
                const index = this.slideIndexFor(mediaId);
                if (index < 0) return;
                this.track.scrollTo({
                    left: index * this.track.clientWidth,
                    behavior: smooth && !VLB_REDUCED_MOTION.matches ? 'smooth' : 'auto',
                });
                this.markActive(mediaId);
            }

            openLightbox(mediaId) {
                const template = this.querySelector(`[data-vlb-lightbox-media="${mediaId}"]`);
                if (!template || !this.lightbox) return;
                this.stage.innerHTML = '';
                this.stage.appendChild(template.content.cloneNode(true));
                this.resetZoom();
                this.lightbox.showModal();
                document.documentElement.style.overflow = 'hidden';
            }

            closeLightbox() {
                // Teardown happens in the dialog's 'close' event handler, so every
                // close path shares the same cleanup.
                if (this.lightbox?.open) this.lightbox.close();
            }

            /* Lightbox gestures, tuned for mobile: focal-point pinch zoom and
               double-tap (zooms where the finger is, not the centre), panning
               clamped to the image bounds, and swipe-down to dismiss at 1x. */
            initGestures() {
                if (!this.stage) return;
                this.zoom = {scale: 1, x: 0, y: 0};
                this.gestureMoved = false;
                const pointers = new Map();
                let pinch = null; // { dist, scale, x, y }
                let pan = null; // { startX, startY }
                let swipe = null; // { startY }
                let lastTap = 0;
                let downAt = null;

                const img = () => this.stage.querySelector('img');

                const clampPan = () => {
                    const el = img();
                    if (!el) return;
                    const maxX = Math.max(0, (el.offsetWidth * this.zoom.scale - this.stage.clientWidth) / 2);
                    const maxY = Math.max(0, (el.offsetHeight * this.zoom.scale - this.stage.clientHeight) / 2);
                    this.zoom.x = Math.min(maxX, Math.max(-maxX, this.zoom.x));
                    this.zoom.y = Math.min(maxY, Math.max(-maxY, this.zoom.y));
                };

                const apply = (animate) => {
                    const el = img();
                    if (!el) return;
                    el.style.transition =
                        animate && !VLB_REDUCED_MOTION.matches ? 'transform 0.2s ease' : '';
                    el.style.transform = `translate(${this.zoom.x}px, ${this.zoom.y}px) scale(${this.zoom.scale})`;
                };

                this.resetZoom = () => {
                    this.zoom = {scale: 1, x: 0, y: 0};
                    this.gestureMoved = false;
                    apply(false);
                };

                // screen point relative to the stage centre (= the transform origin)
                const relPoint = (clientX, clientY) => {
                    const rect = this.stage.getBoundingClientRect();
                    return {
                        x: clientX - rect.left - rect.width / 2,
                        y: clientY - rect.top - rect.height / 2,
                    };
                };

                // zoom to `scale`, keeping the screen point r visually fixed
                const zoomAt = (r, scale, animate) => {
                    const s0 = this.zoom.scale;
                    const s1 = Math.min(5, Math.max(1, scale));
                    this.zoom.x = r.x - (s1 / s0) * (r.x - this.zoom.x);
                    this.zoom.y = r.y - (s1 / s0) * (r.y - this.zoom.y);
                    this.zoom.scale = s1;
                    if (s1 === 1) {
                        this.zoom.x = 0;
                        this.zoom.y = 0;
                    }
                    clampPan();
                    apply(animate);
                };

                this.stage.addEventListener('pointerdown', (e) => {
                    if (!img()) return;
                    this.stage.setPointerCapture?.(e.pointerId);
                    pointers.set(e.pointerId, e);
                    this.gestureMoved = false;
                    downAt = {x: e.clientX, y: e.clientY};

                    if (pointers.size === 2) {
                        const [a, b] = Array.from(pointers.values());
                        pinch = {
                            dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
                            scale: this.zoom.scale,
                            x: this.zoom.x,
                            y: this.zoom.y,
                        };
                        pan = null;
                        swipe = null;
                    } else if (pointers.size === 1) {
                        const now = Date.now();
                        if (now - lastTap < 300) {
                            zoomAt(relPoint(e.clientX, e.clientY), this.zoom.scale > 1 ? 1 : 2.5, true);
                            lastTap = 0;
                            return;
                        }
                        lastTap = now;
                        if (this.zoom.scale > 1) {
                            pan = {startX: e.clientX - this.zoom.x, startY: e.clientY - this.zoom.y};
                        } else {
                            swipe = {startY: e.clientY};
                        }
                    }
                });

                this.stage.addEventListener('pointermove', (e) => {
                    if (!pointers.has(e.pointerId)) return;
                    pointers.set(e.pointerId, e);
                    if (downAt && Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > 10) {
                        this.gestureMoved = true;
                    }

                    if (pointers.size === 2 && pinch) {
                        const [a, b] = Array.from(pointers.values());
                        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
                        const mid = relPoint((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
                        const s1 = Math.min(5, Math.max(1, (pinch.scale * dist) / pinch.dist));
                        this.zoom.x = mid.x - (s1 / pinch.scale) * (mid.x - pinch.x);
                        this.zoom.y = mid.y - (s1 / pinch.scale) * (mid.y - pinch.y);
                        this.zoom.scale = s1;
                        clampPan();
                        apply(false);
                    } else if (pointers.size === 1) {
                        if (pan) {
                            this.zoom.x = e.clientX - pan.startX;
                            this.zoom.y = e.clientY - pan.startY;
                            clampPan();
                            apply(false);
                        } else if (swipe) {
                            // follow the finger at 1x — swipe down (or up) dismisses
                            this.zoom.y = (e.clientY - swipe.startY) * 0.55;
                            apply(false);
                        }
                    }
                });

                const release = (e) => {
                    pointers.delete(e.pointerId);
                    if (pointers.size < 2) pinch = null;
                    if (pointers.size === 0) {
                        if (swipe) {
                            const dy = this.zoom.y / 0.55;
                            swipe = null;
                            if (Math.abs(dy) > 90) {
                                this.closeLightbox();
                                return;
                            }
                            this.zoom.y = 0;
                            apply(true);
                        }
                        pan = null;
                        downAt = null;
                        if (this.zoom.scale <= 1.02) {
                            this.zoom.scale = 1;
                            clampPan();
                            apply(false);
                        }
                    }
                };
                this.stage.addEventListener('pointerup', release);
                this.stage.addEventListener('pointercancel', release);
            }
        }
    );
}